import type { InputHTMLAttributes } from 'react';

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
  /** Error copy; also wires aria-invalid + aria-describedby. */
  error?: string | null;
  /**
   * Guidance that belongs to this field. Wired through `aria-describedby` so
   * it is announced on focus rather than floating unattached beneath the input.
   */
  hint?: string;
}

export function TextField({ id, label, error, hint, ...rest }: TextFieldProps) {
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy =
    [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') ||
    undefined;

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-buds-dark">
        {label}
      </label>
      <input
        {...rest}
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={[
          'mt-2 h-12 w-full rounded-xl border bg-buds-surface px-4 text-base text-buds-dark',
          'placeholder:text-buds-muted',
          'focus-ring',
          error ? 'border-buds-danger' : 'border-buds-muted/80',
        ].join(' ')}
      />
      {error ? (
        <p id={errorId} className="mt-2 text-sm text-buds-danger">
          {error}
        </p>
      ) : null}
      {hint ? (
        <p id={hintId} className="mt-2 text-sm leading-relaxed text-buds-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
